import { isNaturalGasFuel, type Island, type Kitchen, type PanelItem } from '@rta/rta-blueprint-library';

import { getLabelColor, SkuColorPreset } from '../../helpers/sku-labels';
import { sortCompanionsAfterAppliances } from './sort-companions-after-appliances';

export type OverviewAppliance = {
	sku: string;
	label: string;
	isCustomerSupplied: boolean;
};

export type GetOverviewAppliancesParams = {
	island: Island;
	kitchen: Kitchen;
};

// Never listed under islands on the Kitchen Overview — outlets/cutouts/holes get "Non-Standard Changes" entries instead, vents and passthroughs are internal-use only.
const EXCLUDED_CATEGORY_KEYS = new Set(['outlet', 'cutout', 'utility-cutout', 'hole', 'vent', 'passthrough']);

// Grills and Side Burners need a gas hose on a natural-gas install and RTA never ships one; pizza ovens carry the same fuel flag but not the need.
const GAS_HOSE_CATEGORY_IDS = new Set(['d8baa8a0-e20d-4949-883c-927cb1a10bcf', '573d5e2e-94a4-4477-b39a-9e7ef8d613e5']);
const GAS_HOSE_NOTE = ' - Gas Hose Not Included';

// Base skus the product spec (docs/feedback-blueprint-view.txt) lists with no NG gas-hose note — C3CH36 is charcoal and only carries a stale fuel flag.
const NO_GAS_HOSE_NOTE_SKUS = new Set(['C3CH36', 'C3CO50', 'OML75C3', 'OML75C6', 'OML105C3', 'OML105C6']);

/** Rows that make it onto the overview: real SKUs, minus cutout/outlet/vent/passthrough categories (they get "Non-Standard Changes" entries) and anything hidden from the SKU overhead. */
const isOverviewAppliance = (panelItem: PanelItem): boolean => {
	const { item } = panelItem;
	if (!item || !item.fqSku) return false;
	if (EXCLUDED_CATEGORY_KEYS.has(item.category?.key ?? '')) return false;
	return item.showInSkuOverhead !== false;
};

// Customer-supplied = explicitly excluded, OR a hand-entered SKU with no recognized vendor (generic CUSTOM, or an unknown vendorId that Component silently nulled — see component-classes/data/vendor.data.ts: RTA supplies every listed vendor, so any recognized vendor is RTA-supplied regardless of brand), OR flagged RED on the skuOverhead (derived color covers both a per-instance skuColor override and an inherited skuColorPreset).
const isCustomerSuppliedAppliance = (panelItem: PanelItem, isGenericCustomSku: boolean): boolean => {
	const { item } = panelItem;
	const hasNoKnownVendor = !item.vendor || isGenericCustomSku;
	const isRedSku = getLabelColor(panelItem, item.skuColor ?? undefined) === SkuColorPreset.Red;
	return (item.exclude ?? false) || hasNoKnownVendor || isRedSku;
};

/**
 * Collects appliances from an island in the CANONICAL estimate order — mirrors
 * getItemizedMsrpCfn (islands/functions/get-itemized-msrp.cfn.ts) exactly: panels
 * in natural order, groups left-to-right, items top-to-bottom (ascending center.y),
 * labelled with item.blueprintDescription (falling back to label/description). Countertop appliances therefore list before the doors/
 * drawers below them. Excludes cutout/outlet/circular-cutout categories, which have
 * their own "Non-Standard Changes" entries. The one deviation from the estimate order:
 * companion parts (trim kits, sleeves/insulated jackets) are pulled behind the appliance
 * they belong to via sortCompanionsAfterAppliances.
 * Shared with get-blueprint-pages so pagination counts exactly the rows that render.
 */
export const getOverviewAppliances = (params: GetOverviewAppliancesParams): Array<OverviewAppliance> => {
	const { island, kitchen } = params;
	const isNaturalGas = isNaturalGasFuel(kitchen.fuelchoice);
	const appliances: Array<OverviewAppliance> = [];

	island.panels.children.forEach((panel) => {
		const sortedGroups = [...panel.panelItemGroups.children].sort((a, b) => a.center.x - b.center.x);

		sortedGroups.forEach((group) => {
			const sortedItems = [...group.items.children].sort((a, b) => a.center.y - b.center.y).filter(isOverviewAppliance);

			sortCompanionsAfterAppliances(sortedItems).forEach((panelItem) => {
				const { item } = panelItem;
				const isGenericCustomSku = item.fqSku.toUpperCase() === 'CUSTOM';

				// Custom appliance reads "<user SKU>: <description>" (e.g. "HBC54D6AS: Custom
				// Appliance") — the entered SKU lives in customLabel/label, description is generic.
				const sku = isGenericCustomSku
					? (item.customLabel || item.label || item.sku || item.fqSku)
					: item.fqSku;
				const baseLabel = isGenericCustomSku
					? (item.description || 'Custom Appliance')
					: (item.blueprintDescription || item.label || item.description || '');
				const needsGasHoseNote = !isGenericCustomSku && item.fuel && item.showFuelSku && isNaturalGas
					&& GAS_HOSE_CATEGORY_IDS.has(item.category?.id ?? '') && !NO_GAS_HOSE_NOTE_SKUS.has(item.fuelFamily ?? item.sku);
				const label = needsGasHoseNote && baseLabel ? `${baseLabel}${GAS_HOSE_NOTE}` : baseLabel;

				appliances.push({
					sku,
					label,
					isCustomerSupplied: isCustomerSuppliedAppliance(panelItem, isGenericCustomSku)
				});
			});
		});
	});

	return appliances;
};
